import {
  attachWorkspaceServer,
  collectStoredAnnotations,
  loadStoredAnnotations,
  setSaveEnabled,
  setWorkspaceBanner,
  type StoredItem,
} from '../annotations';
import type { StoredResult } from '../annotation-format';
import type { StudyRef } from '../dicomweb';
import type { ViewerShell } from '../shell';
import { confirmDialog, messageDialog, openDialog, paragraph } from '../ui/dialogs';
import { type AnnotationItem, type AnnotationItemIn, type AnnotationVersion, RisError, VersionConflict } from './client';
import { hasPerm, isAuthFailure, isSessionStudy, PERM, risClient } from './session';
import { activeStudyUID, studyByUID } from './util';

/**
 * Server side of the "Anotaciones" workspace (viewer contract §4): each study of the ticket has its own
 * chain of saved versions. The panel (src/annotations.ts) draws; this module loads the latest set at
 * open, saves ("Guardar") the studies whose annotations changed as their next version, lists versions
 * ("Versión"), shows an old one read-only ("Ver versión"), and asks before leaving unsaved changes.
 */

export const SYNC_TEXT = {
  loadError: 'Error al cargar las anotaciones guardadas previamente',
  saveError: 'No se fue posible guardar las anotaciones.',
  authError: 'Error de autorización de usuario para guardar anotaciones.',
  noChanges: 'No hay cambios en las anotaciones para guardar.',
  geometryError: 'Lo sentimos, no se pudo guardar. Compruebe la geometría de la anotación e inténtelo de nuevo',
  unsaved: 'Algunas anotaciones se cambiaron o crearon y no se guardaron. Si cambia la pantalla, se perderá la información de esas anotaciones. ¿Quieres continuar?',
  conflict: 'Otra persona guardó una versión más reciente de las anotaciones.',
  saved: (version: number) => `Anotaciones guardadas (versión ${version}).`,
  viewing: (version: number) => `Viendo versión ${version} (solo lectura)`,
  backToCurrent: 'Volver a la versión actual',
  noVersions: 'No hay versiones guardadas de las anotaciones de este estudio.',
  title: 'Anotaciones',
} as const;

/** One study's chain: the version the workspace was loaded from, and what it looked like then. */
interface StudyChain {
  base: number;
  /** Geometry signature of the study's annotations as loaded / last saved. */
  baseline: string;
  /** Saved items the browser cannot place (series not listed, multi-frame references): kept on save. */
  carried: AnnotationItemIn[];
}

/** A saved version on screen read-only; `stash` is the workspace to put back afterwards. */
interface Viewing {
  study: string;
  version: number;
  stash: StoredItem[];
}

const chains = new Map<string, StudyChain>();
const pending = new Set<string>();
let viewing: Viewing | null = null;
let saving = false;
/** Signature the user already agreed to leave unsaved (the guard does not ask again until it changes). */
let acknowledged = '';
let shellRef: ViewerShell | null = null;

const POINT_DECIMALS = 1e6;

// --- conversions -------------------------------------------------------------------------------

function toStoredItem(item: AnnotationItem): StoredItem {
  return {
    type: item.type,
    points: item.points,
    results: item.results as StoredResult[],
    study_uid: item.study_uid,
    series_uid: item.series_uid ?? '',
    sop_uid: item.sop_uid ?? '',
    frame: item.frame ?? 1,
    slice: item.slice ?? 0,
    slices_number: item.slices_number ?? 0,
    modality: item.modality ?? '',
    title: item.title ?? '',
  };
}

/** Results the contract accepts: `[value, unit, label]` with a finite number or a string value. */
function cleanResults(results: StoredResult[]): StoredResult[] {
  return results.filter(([value]) => typeof value === 'string' || Number.isFinite(value));
}

function toItemIn(item: StoredItem): AnnotationItemIn {
  return {
    series_uid: item.series_uid || null,
    sop_uid: item.sop_uid || null,
    frame: item.frame || null,
    slice: item.slice || null,
    slices_number: item.slices_number || null,
    modality: item.modality || null,
    type: item.type,
    title: item.title || null,
    points: item.points,
    results: cleanResults(item.results),
  };
}

/** Where and what each annotation of a study is (values are recomputed on render, so they are left out). */
function signature(items: StoredItem[], studyUID: string): string {
  return JSON.stringify(
    items
      .filter((item) => item.study_uid === studyUID)
      .map((item) => {
        const text = item.results.find((r) => r[2] === 'text')?.[0] ?? '';
        const points = item.points.map((v) => Math.round(v * POINT_DECIMALS) / POINT_DECIMALS);
        return JSON.stringify([item.series_uid, item.sop_uid, item.frame, item.type, points, text, item.title]);
      })
      .sort(),
  );
}

function workspaceItems(): StoredItem[] {
  return viewing ? viewing.stash : collectStoredAnnotations();
}

/** Studies whose workspace differs from their last loaded / saved version. */
export function dirtyStudies(): string[] {
  if (!hasPerm(PERM.annotate)) return [];
  const items = workspaceItems();
  return [...chains].filter(([uid, chain]) => signature(items, uid) !== chain.baseline).map(([uid]) => uid);
}

function fullSignature(): string {
  const items = workspaceItems();
  return [...chains.keys()].map((uid) => signature(items, uid)).join('|');
}

// --- loading -------------------------------------------------------------------------------------

/** Loads `items` editable, keeping the given studies' unplaceable items to carry on the next save. */
function loadEditable(items: StoredItem[], incoming: Set<StoredItem>): Map<string, AnnotationItemIn[]> {
  const { skipped } = loadStoredAnnotations(items, { readOnly: false });
  const carried = new Map<string, AnnotationItemIn[]>();
  for (const item of skipped) {
    if (!incoming.has(item)) continue;
    const list = carried.get(item.study_uid) ?? [];
    list.push(toItemIn(item));
    carried.set(item.study_uid, list);
  }
  return carried;
}

/** Latest saved set of each new study of the ticket, merged into the workspace. */
async function loadLatest(studies: StudyRef[]): Promise<void> {
  const client = risClient();
  const uids = studies.map((s) => s.studyInstanceUID).filter((uid) => isSessionStudy(uid) && !chains.has(uid) && !pending.has(uid));
  if (!client || uids.length === 0) return;
  uids.forEach((uid) => pending.add(uid));
  const results = await Promise.all(uids.map(async (uid) => {
    try {
      return { uid, set: await client.annotationSet(uid, 'latest') };
    } catch (error) {
      console.warn('[viewer] annotations load failed', uid, error);
      return { uid, set: null };
    }
  }));
  uids.forEach((uid) => pending.delete(uid));
  const incoming = new Set<StoredItem>();
  for (const { set } of results) set?.annotations.map(toStoredItem).forEach((item) => incoming.add(item));
  if (results.some((r) => r.set === null)) shellRef?.toast(SYNC_TEXT.loadError, 'error');
  const merged = [...workspaceItems(), ...incoming];
  let carried = new Map<string, AnnotationItemIn[]>();
  if (viewing) viewing.stash = merged;
  else carried = loadEditable(merged, incoming);
  const after = workspaceItems();
  for (const { uid, set } of results) {
    // A study whose set could not be read stays at version 0: saving it then meets the 409 path.
    chains.set(uid, { base: set?.version ?? 0, baseline: signature(after, uid), carried: carried.get(uid) ?? [] });
  }
}

/** Throws away the local changes of one study and loads its latest version ("Cargar la última versión"). */
async function reloadStudy(uid: string): Promise<void> {
  const client = risClient();
  if (!client) return;
  const set = await client.annotationSet(uid, 'latest');
  const incoming = new Set(set.annotations.map(toStoredItem));
  const others = workspaceItems().filter((item) => item.study_uid !== uid);
  const carried = loadEditable([...others, ...incoming], incoming);
  chains.set(uid, { base: set.version, baseline: signature(collectStoredAnnotations(), uid), carried: carried.get(uid) ?? [] });
}

// --- Guardar ---------------------------------------------------------------------------------

async function onConflict(uid: string): Promise<void> {
  const shell = shellRef;
  if (!shell) return;
  const reload = await confirmDialog(shell, {
    title: SYNC_TEXT.title,
    text: SYNC_TEXT.conflict,
    confirmLabel: 'Cargar la última versión',
    cancelLabel: 'Seguir editando',
    className: 'annot-conflict-dialog',
  });
  if (!reload) return;
  try {
    await reloadStudy(uid);
  } catch (error) {
    console.warn('[viewer] annotations reload failed', error);
    shell.toast(SYNC_TEXT.loadError, 'error');
  }
}

function saveErrorText(error: unknown): string {
  if (isAuthFailure(error)) return SYNC_TEXT.authError;
  if (error instanceof RisError && error.status === 422) return SYNC_TEXT.geometryError;
  return SYNC_TEXT.saveError;
}

/** "Guardar": every changed study of the ticket becomes its next version. */
export async function save(): Promise<void> {
  const shell = shellRef;
  const client = risClient();
  if (!shell || !client || viewing || saving || !hasPerm(PERM.annotate)) return;
  const items = collectStoredAnnotations();
  const changed = [...chains].filter(([uid, chain]) => signature(items, uid) !== chain.baseline);
  if (changed.length === 0) {
    shell.toast(SYNC_TEXT.noChanges);
    return;
  }
  saving = true;
  setSaveEnabled(false);
  const versions: number[] = [];
  try {
    for (const [uid, chain] of changed) {
      const body = [...items.filter((item) => item.study_uid === uid).map(toItemIn), ...chain.carried];
      try {
        const set = await client.saveAnnotationSet(uid, chain.base, body);
        chain.base = set.version;
        chain.baseline = signature(items, uid);
        versions.push(set.version);
      } catch (error) {
        if (error instanceof VersionConflict) {
          await onConflict(uid);
        } else {
          console.warn('[viewer] annotations save failed', uid, error);
          messageDialog(shell, SYNC_TEXT.title, saveErrorText(error), 'annot-error-dialog');
        }
        return;
      }
    }
    if (versions.length > 0) shell.toast(SYNC_TEXT.saved(Math.max(...versions)));
  } finally {
    saving = false;
    setSaveEnabled(viewing === null);
  }
}

// --- Versión / Ver versión ---------------------------------------------------------------------

/** The study the version actions act on: the active cell's, if the ticket covers it. */
function currentStudy(): string | undefined {
  const shell = shellRef;
  if (!shell) return undefined;
  const uid = activeStudyUID(shell);
  return chains.has(uid) ? uid : chains.keys().next().value;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function studyCaption(uid: string): string {
  const study = shellRef ? studyByUID(shellRef, uid) : undefined;
  return study ? [study.studyDescription, study.studyDate].filter(Boolean).join(' · ') : uid;
}

async function fetchVersions(uid: string): Promise<AnnotationVersion[] | null> {
  try {
    return await (risClient()?.annotationVersions(uid) ?? Promise.resolve([]));
  } catch (error) {
    console.warn('[viewer] annotation versions failed', error);
    shellRef?.toast(SYNC_TEXT.loadError, 'error');
    return null;
  }
}

/** "Versión": every saved version of the study (version, author, date, count), each viewable. */
export async function showVersions(): Promise<void> {
  const shell = shellRef;
  const uid = currentStudy();
  if (!shell || !uid) return;
  const versions = await fetchVersions(uid);
  if (!versions) return;
  const { body, close } = openDialog(shell, {
    title: 'Versiones de las anotaciones',
    className: 'annot-versions-dialog',
    buttons: [{ label: 'Cerrar', primary: true, onClick: (close) => close() }],
  });
  if (chains.size > 1) paragraph(body, studyCaption(uid), 'annot-versions-study');
  if (versions.length === 0) {
    paragraph(body, SYNC_TEXT.noVersions, 'annot-versions-empty');
    return;
  }
  const table = document.createElement('table');
  table.className = 'annot-versions-table';
  const head = table.createTHead().insertRow();
  for (const label of ['Versión', 'Autor', 'Fecha', 'Anotaciones', '']) {
    const th = document.createElement('th');
    th.textContent = label;
    head.appendChild(th);
  }
  const rows = table.createTBody();
  for (const version of versions) {
    const row = rows.insertRow();
    row.dataset.version = String(version.version);
    row.insertCell().textContent = String(version.version);
    row.insertCell().textContent = version.created_by_name || version.created_by;
    row.insertCell().textContent = formatDate(version.created_at);
    row.insertCell().textContent = String(version.count);
    const view = document.createElement('button');
    view.type = 'button';
    view.className = 'cmd-dialog-button annot-version-view';
    view.textContent = 'Ver';
    view.addEventListener('click', () => {
      close();
      void viewVersion(uid, version.version);
    });
    row.insertCell().appendChild(view);
  }
  body.appendChild(table);
}

/** "Ver versión": pick a saved version and show it read-only. */
export async function pickVersion(): Promise<void> {
  const shell = shellRef;
  const uid = currentStudy();
  if (!shell || !uid) return;
  const versions = await fetchVersions(uid);
  if (!versions) return;
  if (versions.length === 0) {
    messageDialog(shell, 'Ver versión', SYNC_TEXT.noVersions, 'annot-pick-dialog');
    return;
  }
  const select = document.createElement('select');
  select.className = 'annot-version-select';
  for (const version of versions) {
    const option = document.createElement('option');
    option.value = String(version.version);
    option.textContent = `Versión ${version.version} · ${version.created_by_name || version.created_by} · ${formatDate(version.created_at)}`;
    select.appendChild(option);
  }
  const { body } = openDialog(shell, {
    title: 'Ver versión',
    className: 'annot-pick-dialog',
    buttons: [
      { label: 'Cancelar', onClick: (close) => close() },
      { label: 'Ver', primary: true, onClick: (close) => { close(); void viewVersion(uid, Number(select.value)); } },
    ],
  });
  if (chains.size > 1) paragraph(body, studyCaption(uid), 'annot-versions-study');
  body.appendChild(select);
}

/** Shows saved version `version` of `uid` read-only; the workspace is put aside until "Volver…". */
export async function viewVersion(uid: string, version: number): Promise<void> {
  const shell = shellRef;
  const client = risClient();
  if (!shell || !client) return;
  let items: StoredItem[];
  try {
    items = (await client.annotationSet(uid, version)).annotations.map(toStoredItem);
  } catch (error) {
    console.warn('[viewer] annotation version failed', error);
    shell.toast(SYNC_TEXT.loadError, 'error');
    return;
  }
  if (!viewing) viewing = { study: uid, version, stash: collectStoredAnnotations() };
  viewing.study = uid;
  viewing.version = version;
  loadStoredAnnotations(items, { readOnly: true });
  setSaveEnabled(false);
  setWorkspaceBanner({ text: SYNC_TEXT.viewing(version), actionLabel: SYNC_TEXT.backToCurrent, onAction: returnToCurrent });
  shell.setRightDrawer(true);
}

/** Back from a read-only version to the workspace as it was (unsaved changes included). */
export function returnToCurrent(): void {
  if (!viewing) return;
  const { stash } = viewing;
  viewing = null;
  loadStoredAnnotations(stash, { readOnly: false });
  setWorkspaceBanner(null);
  setSaveEnabled(true);
}

// --- unsaved-changes guard ---------------------------------------------------------------------

function guard(shell: ViewerShell, proceed: () => void): void {
  const current = fullSignature();
  if (dirtyStudies().length === 0 || current === acknowledged) {
    proceed();
    return;
  }
  void confirmDialog(shell, {
    title: SYNC_TEXT.title,
    text: SYNC_TEXT.unsaved,
    confirmLabel: 'Continuar',
    cancelLabel: 'Cancelar',
    className: 'annot-unsaved-dialog',
  }).then((go) => {
    if (!go) return;
    acknowledged = current;
    proceed();
  });
}

interface RisAnnotationsDebug {
  dirty(): string[];
  base(studyUID: string): number | undefined;
  viewing(): { study: string; version: number } | null;
  save(): Promise<void>;
}

declare global {
  interface Window {
    __viewerRisAnnotations?: RisAnnotationsDebug;
  }
}

export function install(shell: ViewerShell): void {
  shellRef = shell;
  attachWorkspaceServer({
    canSave: hasPerm(PERM.annotate),
    save: () => void save(),
    showVersions: () => void showVersions(),
    viewVersion: () => void pickVersion(),
  });
  void loadLatest(shell.studies());
  shell.on('studies-changed', ({ studies }) => void loadLatest(studies));
  if (hasPerm(PERM.annotate)) {
    shell.setNavigationGuard((proceed) => guard(shell, proceed));
    window.addEventListener('beforeunload', (event) => {
      if (dirtyStudies().length === 0) return;
      event.preventDefault();
      // Legacy browsers only prompt when returnValue is set; the text is theirs, never ours.
      event.returnValue = true;
    });
  }
  window.__viewerRisAnnotations = {
    dirty: dirtyStudies,
    base: (uid) => chains.get(uid)?.base,
    viewing: () => (viewing ? { study: viewing.study, version: viewing.version } : null),
    save,
  };
}
